/**
 * UrssafTab — 08/02/2026 Q2 user request (P3 onglet Fiscalité).
 *
 * Affiche les déclarations trimestrielles URSSAF passées + estimation
 * du trimestre en cours. Pédagogique : rappelle que les cotisations
 * sont déjà prélevées dans la masse salariale quotidienne (employerCost)
 * et que ce flux est administratif (échéances 15 mars/juin/sept/déc).
 */
import React from 'react';
import { Scale, Calendar, Users, Coins, AlertCircle } from 'lucide-react';
import useGameStore from '../../store/gameStore';
import { computeUrssafEstimate, quarterLabelFromDeclarationMonth } from '../../store/helpers/urssaf';

const QUARTER_MONTHS = {
  1: 'Janv. – Mars',
  2: 'Avr. – Juin',
  3: 'Juil. – Sept.',
  4: 'Oct. – Déc.',
};

export default function UrssafTab() {
  const urssafHistory = useGameStore(s => s.urssafHistory) || [];
  const currentMonth = useGameStore(s => s.month) || 1;
  const currentYear = useGameStore(s => s.year) || 2026;
  const drivers = useGameStore(s => s.drivers) || [];
  const hiredSupport = useGameStore(s => s.hiredSupport) || {};

  const est = computeUrssafEstimate({ drivers, hiredSupport });
  const currentQuarter = Math.ceil(currentMonth / 3);

  // Find next declaration date (months 1, 4, 7, 10).
  // When current month is 12, `find` returns undefined → fallback to
  // January (months[0] = 1) of next year. We use `+12` so the year
  // rollover detection below (m > 12) works correctly.
  const nextDeclarationMonth = (() => {
    const months = [1, 4, 7, 10];
    return months.find(m => m > currentMonth) || (months[0] + 12);
  })();
  const nextDeclarationMonthIn = nextDeclarationMonth > 12 ? nextDeclarationMonth - 12 : nextDeclarationMonth;
  const nextDeclarationYear = nextDeclarationMonth > 12 ? currentYear + 1 : currentYear;
  const nextLabel = quarterLabelFromDeclarationMonth(nextDeclarationMonthIn, nextDeclarationYear);

  return (
    <div className="space-y-4" data-testid="urssaf-tab">
      {/* Pedagogical banner */}
      <div className="bg-sky-500/5 border border-sky-500/30 rounded-md px-3 py-2 flex items-start gap-2" data-testid="urssaf-info-banner">
        <AlertCircle className="w-4 h-4 text-sky-300 flex-shrink-0 mt-0.5" />
        <div className="text-[11px] text-slate-300 leading-snug">
          <strong>Information pédagogique.</strong> Les cotisations URSSAF (≈ 42 % de la
          masse salariale) sont <strong>déjà prélevées au fil de l&apos;eau</strong> via le coût
          employeur quotidien (employerCost). Ce tableau matérialise le rythme
          administratif : reminder 15 j avant échéance, déclaration le 1er du mois
          suivant. Aucun débit supplémentaire n&apos;est appliqué.
        </div>
      </div>

      {/* Current quarter estimate */}
      <div className="surface-card p-4" data-testid="urssaf-current-quarter">
        <div className="flex items-center gap-2 mb-3">
          <Scale className="w-4 h-4 text-amber-300" />
          <h3 className="text-sm font-bold text-foreground">
            Trimestre en cours — T{currentQuarter} {currentYear}
            <span className="text-[10px] font-normal text-slate-400 ml-2">
              ({QUARTER_MONTHS[currentQuarter]})
            </span>
          </h3>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <Metric Icon={Users} label="Effectif déclaré" value={est.headcount} unit={est.headcount > 1 ? 'personnes' : 'personne'} />
          <Metric Icon={Coins} label="Masse salariale (3 mois)" value={Math.round(est.monthlyPayroll * 3).toLocaleString('fr-FR')} unit="€" />
          <Metric Icon={Scale} label="Estimation URSSAF" value={est.quarterlyUrssaf.toLocaleString('fr-FR')} unit="€" emphasis />
        </div>
        {nextLabel && (
          <p className="mt-3 text-[11px] text-slate-400">
            <Calendar className="w-3 h-3 inline mr-1" />
            Prochaine déclaration : <strong>{nextLabel.label}</strong> — à compter du 1er {monthName(nextDeclarationMonthIn)} {nextDeclarationYear}.
            Reminder envoyé 15 jours avant l&apos;échéance.
          </p>
        )}
      </div>

      {/* History */}
      <div className="surface-card p-4" data-testid="urssaf-history">
        <div className="flex items-center gap-2 mb-3">
          <Calendar className="w-4 h-4 text-emerald-300" />
          <h3 className="text-sm font-bold text-foreground">
            Historique des déclarations ({urssafHistory.length})
          </h3>
        </div>
        {urssafHistory.length === 0 ? (
          <div className="text-center py-6 text-[11px] text-slate-500" data-testid="urssaf-history-empty">
            Aucune déclaration trimestrielle pour le moment.
            <br />
            La 1ère se déclenchera au 1er du mois suivant la fin du trimestre en cours.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-[11px]">
              <thead>
                <tr className="text-[10px] uppercase tracking-wider text-slate-400 border-b border-ink-600">
                  <th className="text-left py-1 pr-3">Trimestre</th>
                  <th className="text-right py-1 pr-3">Effectif</th>
                  <th className="text-right py-1 pr-3">Cotisations URSSAF</th>
                  <th className="text-right py-1">Déclaré au jour</th>
                </tr>
              </thead>
              <tbody>
                {urssafHistory.slice().reverse().map((entry, idx) => (
                  <tr
                    key={`${entry.year}-Q${entry.quarter}-${idx}`}
                    className="border-b border-ink-700/60"
                    data-testid={`urssaf-history-row-${entry.year}-Q${entry.quarter}`}
                  >
                    <td className="py-2 pr-3 text-emerald-200 font-semibold">
                      T{entry.quarter} {entry.year}
                    </td>
                    <td className="py-2 pr-3 text-right text-slate-300">{entry.headcount}</td>
                    <td className="py-2 pr-3 text-right text-amber-300 font-mono">
                      {entry.amount.toLocaleString('fr-FR')} €
                    </td>
                    <td className="py-2 text-right text-slate-400 font-mono">
                      J{entry.declaredOnDay}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function Metric({ Icon, label, value, unit, emphasis }) {
  return (
    <div className={`rounded-lg p-3 border ${emphasis ? 'bg-amber-500/10 border-amber-500/40' : 'bg-ink-800 border-ink-600'}`}>
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-slate-400 mb-1">
        <Icon className="w-3 h-3" />
        {label}
      </div>
      <p className={`text-lg font-mono font-bold ${emphasis ? 'text-amber-200' : 'text-foreground'}`}>
        {value}
        <span className="text-[10px] font-normal text-slate-400 ml-1">{unit}</span>
      </p>
    </div>
  );
}

function monthName(m) {
  return ['janvier', 'février', 'mars', 'avril', 'mai', 'juin',
          'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre'][m - 1] || '';
}
